/**
 * Central Logging Utility
 * Captures frontend errors and sends them to the backend logging API
 */

import { API_BASE } from '../config/api';

// Generate correlation ID for this session
let correlationId = generateCorrelationId();
let currentRoute = window.location.pathname;

// Track if logger is initialized
let isInitialized = false;

/**
 * Generate a unique correlation ID
 */
function generateCorrelationId() {
  return `frontend-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Get current user context
 */
function getUserContext() {
  // Check for CRM admin token
  const crmToken = localStorage.getItem('crm_access_token');
  if (crmToken) {
    // Try to decode token to get user info (basic check)
    try {
      const payload = JSON.parse(atob(crmToken.split('.')[1]));
      if (payload.role === 'crm_admin') {
        return {
          user_type: 'crm_admin',
          user_id: payload.user_id || payload.sub,
          role: 'crm_admin'
        };
      } else if (payload.role === 'counselor') {
        return {
          user_type: 'counselor',
          user_id: payload.user_id || payload.sub,
          role: 'counselor'
        };
      }
    } catch (e) {
      // Token decode failed, continue with other checks
    }
  }
  
  // Fallback to old admin token check
  const adminToken = localStorage.getItem('admin_token');
  const adminEmail = localStorage.getItem('admin_email');
  
  if (adminToken && adminEmail) {
    return {
      user_type: 'admin',
      user_id: adminEmail
    };
  }
  
  return {
    user_type: 'visitor',
    user_id: null
  };
}

/**
 * Determine domain based on current route
 */
function getDomain() {
  return window.location.pathname.startsWith('/admin') ? 'admin' : 'public';
}

/**
 * Determine if we're in CRM admin or counselor UI
 */
function getCrmErrorType() {
  const path = window.location.pathname;
  if (path.startsWith('/crm/admin')) {
    return 'frontend_admin';
  } else if (path.startsWith('/crm/counselor')) {
    return 'frontend_counselor';
  } else if (path.startsWith('/crm/')) {
    return 'frontend_crm';
  }
  return null;
}

/**
 * Sanitize data to remove sensitive information
 */
function sanitizeData(data) {
  if (!data || typeof data !== 'object') {
    return data;
  }
  
  const sensitiveFields = ['password', 'password_hash', 'token', 'access_token', 'refresh_token', 'api_key', 'secret', 'authorization'];
  const sanitized = {};
  
  for (const [key, value] of Object.entries(data)) {
    const keyLower = key.toLowerCase();
    if (sensitiveFields.some(field => keyLower.includes(field))) {
      sanitized[key] = '***REDACTED***';
    } else if (typeof value === 'object' && value !== null) {
      sanitized[key] = sanitizeData(value);
    } else {
      sanitized[key] = value;
    }
  }
  
  return sanitized;
}

/**
 * Send log to backend (non-blocking)
 */
async function sendLog(logData) {
  try {
    // Don't log if we're on the logging endpoint itself (avoid infinite loops)
    if (window.location.pathname.includes('/admin/system-logs')) {
      return;
    }
    
    const response = await fetch(`${API_BASE}/api/system/logs`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Correlation-ID': correlationId
      },
      body: JSON.stringify(logData)
    });
    
    // Don't throw errors - logging failures should be silent
    if (!response.ok) {
      console.warn('Failed to send log to backend:', response.status);
    }
  } catch (error) {
    // Silently fail - don't break the app if logging fails
    console.warn('Error sending log:', error);
  }
}

/**
 * Map CRM user types to system log user types
 */
function mapUserTypeToSystemLog(userType) {
  if (userType === 'crm_admin' || userType === 'admin') {
    return 'admin';
  }
  // Counselors and other types are treated as visitors for system logs
  return 'visitor';
}

/**
 * Log an error
 */
export function logError(message, error = null, metadata = {}) {
  const userContext = getUserContext();
  const domain = getDomain();
  
  // Check if this is a CRM error (admin or counselor UI)
  const crmErrorType = getCrmErrorType();
  
  if (crmErrorType) {
    // Send to CRM error logs
    sendCrmErrorLog(crmErrorType, message, error, userContext, metadata);
  }
  
  // Also send to general system logs
  const logData = {
    domain,
    app: 'web',
    severity: 'error',
    error_type: error ? error.name : 'Error',
    message: message || (error ? error.message : 'Unknown error'),
    route: currentRoute,
    user_type: mapUserTypeToSystemLog(userContext.user_type),
    user_id: userContext.user_id,
    correlation_id: correlationId,
    metadata: sanitizeData({
      ...metadata,
      userAgent: navigator.userAgent,
      url: window.location.href
    }),
    stack_trace: error ? error.stack : null
  };
  
  // Send log (non-blocking)
  sendLog(logData);
  
  // Also log to console in development
  if (process.env.NODE_ENV === 'development') {
    console.error('[Logger]', logData);
  }
}

/**
 * Send error log to CRM error logs endpoint
 * Non-blocking - must not crash UI if it fails
 */
async function sendCrmErrorLog(errorType, message, error, userContext, metadata) {
  try {
    const { API_BASE } = require('../config/api');
    const crmToken = localStorage.getItem('crm_access_token');
    
    const errorLogData = {
      error_type: errorType,
      severity: 'critical', // Frontend errors are typically critical
      error_message: message || (error ? error.message : 'Unknown error'),
      user_role: userContext.role || userContext.user_type || null,
      user_id: userContext.user_id || null,
      action_context: metadata.action_context || currentRoute || null,
      endpoint: metadata.endpoint || window.location.pathname || null,
      stack_trace: error ? error.stack : null
    };
    
    // Send to CRM error logs (no auth required, but include token if available)
    const headers = {
      'Content-Type': 'application/json'
    };
    
    if (crmToken) {
      headers['Authorization'] = `Bearer ${crmToken}`;
    }
    
    // Use fetch directly (not crmFetch) to avoid circular dependency
    // This is a fire-and-forget operation - don't wait for response
    fetch(`${API_BASE}/api/crm/admin/error-logs`, {
      method: 'POST',
      headers,
      body: JSON.stringify(errorLogData)
    }).catch(err => {
      // Silently fail - don't log to console to avoid noise
      // Error logging failures should be completely silent
    });
  } catch (err) {
    // Silently fail - must not crash UI
    // Error logging failures should be completely silent
  }
}

/**
 * Log a warning
 */
export function logWarning(message, metadata = {}) {
  const userContext = getUserContext();
  const domain = getDomain();
  
  const logData = {
    domain,
    app: 'web',
    severity: 'warning',
    message,
    route: currentRoute,
    user_type: mapUserTypeToSystemLog(userContext.user_type),
    user_id: userContext.user_id,
    correlation_id: correlationId,
    metadata: sanitizeData(metadata)
  };
  
  sendLog(logData);
  
  if (process.env.NODE_ENV === 'development') {
    console.warn('[Logger]', logData);
  }
}

/**
 * Log an info message
 */
export function logInfo(message, metadata = {}) {
  const userContext = getUserContext();
  const domain = getDomain();
  
  const logData = {
    domain,
    app: 'web',
    severity: 'info',
    message,
    route: currentRoute,
    user_type: mapUserTypeToSystemLog(userContext.user_type),
    user_id: userContext.user_id,
    correlation_id: correlationId,
    metadata: sanitizeData(metadata)
  };
  
  sendLog(logData);
  
  if (process.env.NODE_ENV === 'development') {
    console.info('[Logger]', logData);
  }
}

/**
 * Log an audit event
 */
export function logAudit(message, metadata = {}) {
  const userContext = getUserContext();
  const domain = getDomain();
  
  const logData = {
    domain,
    app: 'web',
    severity: 'audit',
    message,
    route: currentRoute,
    user_type: userContext.user_type,
    user_id: userContext.user_id,
    correlation_id: correlationId,
    metadata: sanitizeData(metadata)
  };
  
  sendLog(logData);
}

/**
 * Update current route (call this when route changes)
 */
export function updateRoute(route) {
  currentRoute = route;
}

/**
 * Get current correlation ID
 */
export function getCorrelationId() {
  return correlationId;
}

/**
 * Initialize error handlers
 */
export function initializeLogger() {
  if (isInitialized) {
    return;
  }
  
  isInitialized = true;
  
  // Capture uncaught JavaScript errors
  window.addEventListener('error', (event) => {
    logError(
      event.message || 'Uncaught JavaScript error',
      event.error || new Error(event.message),
      {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno
      }
    );
    
    // Show friendly error message to user (don't expose stack trace)
    if (getDomain() === 'public') {
      // Only show on public site, not admin UI
      console.error('An error occurred. Please refresh the page or contact support if the problem persists.');
    }
  });
  
  // Capture unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    const error = event.reason instanceof Error 
      ? event.reason 
      : new Error(String(event.reason));
    
    logError(
      `Unhandled promise rejection: ${error.message}`,
      error,
      {
        reason: String(event.reason)
      }
    );
    
    // Prevent default browser error handling
    event.preventDefault();
  });
  
  // Intercept fetch API calls to capture failed requests
  const originalFetch = window.fetch;
  window.fetch = async function(...args) {
    const url = args[0];
    const options = args[1] || {};
    
    // Add correlation ID to headers
    if (!options.headers) {
      options.headers = {};
    }
    if (typeof options.headers === 'object' && !(options.headers instanceof Headers)) {
      options.headers['X-Correlation-ID'] = correlationId;
    }
    
    try {
      const response = await originalFetch.apply(this, args);
      
      // Log failed API calls (4xx, 5xx)
      if (!response.ok && !url.includes('/api/system/logs')) {
        const userContext = getUserContext();
        const domain = getDomain();
        
        // Try to get error message from response
        let errorMessage = `API request failed: ${response.status} ${response.statusText}`;
        try {
          const errorData = await response.clone().json().catch(() => null);
          if (errorData && errorData.detail) {
            errorMessage = errorData.detail;
          }
        } catch {
          // Ignore JSON parse errors
        }
        
        const logData = {
          domain,
          app: 'web',
          severity: response.status >= 500 ? 'error' : 'warning',
          error_type: 'APIError',
          message: errorMessage,
          route: currentRoute,
          api_endpoint: url,
          http_status: response.status,
          user_type: userContext.user_type,
          user_id: userContext.user_id,
          correlation_id: correlationId,
          metadata: sanitizeData({
            method: options.method || 'GET',
            url: url
          })
        };
        
        sendLog(logData);
      }
      
      return response;
    } catch (error) {
      // Log network errors
      if (!url.includes('/api/system/logs')) {
        logError(
          `Network error: ${error.message}`,
          error,
          {
            url,
            method: options.method || 'GET'
          }
        );
      }
      
      throw error;
    }
  };
  
  // Update route on navigation (for React Router)
  if (window.history && window.history.pushState) {
    const originalPushState = window.history.pushState;
    window.history.pushState = function(...args) {
      originalPushState.apply(this, args);
      updateRoute(window.location.pathname);
    };
    
    window.addEventListener('popstate', () => {
      updateRoute(window.location.pathname);
    });
  }
  
  logInfo('Logger initialized', { correlation_id: correlationId });
}

// Auto-initialize on import
if (typeof window !== 'undefined') {
  initializeLogger();
}
